import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, Waves, Loader2, BookOpenCheck, MapPin, Gauge, Thermometer,
  Hourglass, Clock, Copy, RefreshCw, Anchor,
} from 'lucide-react';
import { toast } from 'sonner';
import PwaInstallBanner from '../../components/PwaInstallBanner';
import { viewerStore } from './viewerStore';
import { formatDives, LOG_FORMATS } from './diveLogExports';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pull every saved share, find which launches the diver is on, attach the log.
const collectMyDives = (shares, diverName) => {
  const out = [];
  shares.forEach((share) => {
    const data = share.data;
    if (!data) return;
    (data.launches || []).forEach((L) => {
      const onLaunch = (L.divers || []).some((d) => (d.name || '').trim().toLowerCase() === diverName.trim().toLowerCase());
      if (!onLaunch) return;
      const launchLabel = L.launch_in_boat != null
        ? `${L.boat_name || 'Boat'} · Launch ${L.launch_in_boat + 1}`
        : `Launch ${L.index + 1}`;
      const time = (data.display_times && data.display_times[L.index]) || L.log?.time || '';
      const cert = (L.divers || []).find((d) => (d.name || '').toLowerCase() === diverName.toLowerCase())?.certification;
      // We only output dives with an instructor log (since those are the ones with verifiable data).
      if (L.log) {
        out.push({
          trip_name: data.trip_name,
          launch_label: launchLabel,
          date: L.log.date || data.date,
          time: L.log.time || time,
          site: L.log.site,
          depth_m: L.log.depth_m,
          water_temp_c: L.log.water_temp_c,
          duration_min: L.log.duration_min,
          instructor: L.log.recorded_by_name || 'Crew',
          buddy: null, // not in share payload; could be inferred later
          certification: cert,
        });
      }
    });
  });
  // Sort by date+time ascending
  out.sort((a, b) => `${a.date} ${a.time || ''}`.localeCompare(`${b.date} ${b.time || ''}`));
  return out;
};

const PlannerMyDives = () => {
  const [shares, setShares] = useState([]); // [{ token, trip_name, date, data }]
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [diverName, setDiverName] = useState(viewerStore.lastClaimedName() || '');
  const [format, setFormat] = useState('padi');

  const loadAll = async () => {
    const stored = viewerStore.list();
    setRefreshing(true);
    const results = await Promise.all(stored.map(async (s) => {
      try {
        const r = await axios.get(`${API_URL}/planner/share/${s.token}`);
        return { ...s, data: r.data };
      } catch (e) {
        return { ...s, data: null, error: true };
      }
    }));
    setShares(results);
    setRefreshing(false);
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-line */ }, []);

  // Collect candidate names across all loaded shares to power the picker.
  const candidateNames = (() => {
    const set = new Set();
    shares.forEach((s) => {
      (s.data?.launches || []).forEach((L) => (L.divers || []).forEach((d) => {
        if (d.name) set.add(d.name);
      }));
    });
    return Array.from(set).sort();
  })();

  const myDives = diverName ? collectMyDives(shares, diverName) : [];

  const handleSetName = (n) => {
    setDiverName(n);
    // Persist this name against every share where the name appears, so future visits remember it.
    shares.forEach((s) => {
      const namesOnShare = (s.data?.launches || []).flatMap((L) => (L.divers || []).map((d) => d.name));
      if (namesOnShare.some((nm) => (nm || '').toLowerCase() === n.toLowerCase())) {
        viewerStore.claimName(s.token, n);
      }
    });
  };

  const copyToClipboard = async () => {
    if (!diverName || myDives.length === 0) { toast.error('Nothing to copy yet'); return; }
    const text = formatDives(diverName, myDives, format);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${myDives.length} dive${myDives.length === 1 ? '' : 's'} to clipboard`);
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Copied to clipboard'); } catch (_) { toast.error('Could not copy'); }
      ta.remove();
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-32" data-testid="planner-my-dives">
      <div className="bg-gradient-to-br from-[#0A2342] to-[#1a3a5c] text-white px-5 pt-8 pb-7">
        <div className="max-w-3xl mx-auto">
          <Link to="/planner/viewer" className="text-xs opacity-80 flex items-center gap-1 mb-2 hover:opacity-100">
            <ChevronLeft className="w-3.5 h-3.5" /> My saved schedules
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <Waves className="w-7 h-7 text-[#FF6B35]" />
            <h1 className="text-xl md:text-2xl font-bold">My Logged Dives</h1>
            <button
              onClick={loadAll}
              disabled={refreshing}
              className="ml-auto text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
              data-testid="my-dives-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
          <p className="text-xs opacity-80">
            All dives the instructor recorded for you, across every trip you've saved. Use the export to copy them into your PADI / SSI / NAUI / ISC log book.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-4 space-y-4">
        {/* Name picker */}
        <div className="bg-white border rounded-2xl p-4 space-y-2" data-testid="name-picker-card">
          <label className="text-xs text-gray-500 block">Which diver are you?</label>
          {candidateNames.length === 0 ? (
            <p className="text-xs text-gray-500">No schedules saved yet. Open a share link from your dive operator first.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {candidateNames.map((nm) => (
                <button
                  key={nm}
                  onClick={() => handleSetName(nm)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition ${
                    nm.toLowerCase() === (diverName || '').toLowerCase()
                      ? 'bg-[#0A2342] text-white border-[#0A2342]'
                      : 'bg-white text-[#0A2342] border-slate-300 hover:border-[#FF6B35]'
                  }`}
                  data-testid={`name-pick-${nm.replace(/\s+/g, '-')}`}
                >
                  {nm}
                </button>
              ))}
            </div>
          )}
        </div>

        {!diverName ? (
          <div className="bg-white border rounded-2xl p-8 text-center text-sm text-gray-500">
            Pick your name above to see your dives.
          </div>
        ) : myDives.length === 0 ? (
          <div className="bg-white border rounded-2xl p-8 text-center text-sm text-gray-500">
            <BookOpenCheck className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            No instructor-recorded dives yet for <strong>{diverName}</strong>. Once the dive master fills in the log after each launch, your dives will appear here.
          </div>
        ) : (
          <>
            {/* Export bar */}
            <div className="bg-white border rounded-2xl p-3 flex flex-wrap items-center justify-between gap-2" data-testid="my-dives-export-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-gray-500">Format:</label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="border rounded-lg px-2 py-1 text-sm"
                  data-testid="my-dives-format-select"
                >
                  {LOG_FORMATS.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={copyToClipboard}
                className="text-sm bg-[#FF6B35] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#FF6B35]/90"
                data-testid="my-dives-copy-btn"
              >
                <Copy className="w-4 h-4" /> Copy {myDives.length} dive{myDives.length === 1 ? '' : 's'} to log book
              </button>
            </div>

            {/* Dive cards */}
            <div className="space-y-3">
              {myDives.map((d, i) => (
                <div key={`md-${i}`} className="bg-white border rounded-2xl p-4" data-testid={`my-dive-card-${i}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-[#FF6B35] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <div>
                        <div className="font-semibold text-[#0A2342]">{d.site || 'Unknown site'}</div>
                        <div className="text-[11px] text-gray-500 flex items-center gap-1"><Anchor className="w-3 h-3" /> {d.trip_name} · {d.launch_label}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 text-right">
                      <div>{d.date}</div>
                      {d.time && <div>{d.time}</div>}
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 italic leading-snug mb-2">
                    NOTE: The following information is from <strong>{d.instructor}</strong> and is here for reference only.
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#0A2342]">
                    {d.depth_m != null && <span><Gauge className="w-3.5 h-3.5 inline text-[#FF6B35]" /> {d.depth_m} m</span>}
                    {d.duration_min != null && <span><Hourglass className="w-3.5 h-3.5 inline text-[#FF6B35]" /> {d.duration_min} min</span>}
                    {d.water_temp_c != null && <span><Thermometer className="w-3.5 h-3.5 inline text-[#FF6B35]" /> {d.water_temp_c}°C</span>}
                    {d.time && <span><Clock className="w-3.5 h-3.5 inline text-[#FF6B35]" /> {d.time}</span>}
                    {d.site && <span><MapPin className="w-3.5 h-3.5 inline text-[#FF6B35]" /> {d.site}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <PwaInstallBanner />
    </div>
  );
};

export default PlannerMyDives;
